import { Localized, useLocalization } from "@fluent/react";
import { Button, ButtonGroup, Card, CardContent, Collapse, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { loadCompanies, Company } from "../company/companiesSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { LoadingContainer } from "../../app/LoadingContainer";
import { selectSettings } from "../settings/settingsSlice";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";
import { MonthFilterMenu } from "../documents/utils/MonthFilterMenu";
import SyncIcon from '@mui/icons-material/Sync';
import { fetchAITrace, fetchAIRequests, AICallMetrics, AIRequestTrace } from './aiRequestsApi';
import { showError } from "../notifications/notificationsSlice";

export const AIRequests = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch();

    const [requests, setRequests] = useState<AICallMetrics[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedRow, setSelectedRow] = useState<AICallMetrics | null>(null);
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [traceData, setTraceData] = useState<AIRequestTrace | null>(null);
    const [traceLoading, setTraceLoading] = useState(false);
    const [expandedMessage, setExpandedMessage] = useState(false);
    const [expandedResponse, setExpandedResponse] = useState(false);

    const [company, setCompany] = useState('all');

    useEffect(() => {
        dispatch(loadCompanies())
            .then(unwrapResult)
            .then(setCompanies)
            .catch((error) => console.error(error));
    }, [dispatch]);

    const fetchAIUsageData = useCallback(async (companyId?: string, dateFromParam?: Date | null, dateToParam?: Date | null) => {
        setIsLoading(true);
        try {
            const result = await dispatch(fetchAIRequests({
                companyId: companyId || company,
                dateFrom: dateFromParam !== undefined ? dateFromParam || undefined : dateFrom || undefined,
                dateTo: dateToParam !== undefined ? dateToParam || undefined : dateTo || undefined,
            }));

            if (fetchAIRequests.fulfilled.match(result)) {
                setRequests(result.payload as AICallMetrics[]);
            } else if (fetchAIRequests.rejected.match(result)) {
                dispatch(showError("error"))
            }
        } catch {
            dispatch(showError("error"))
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, company, dateFrom, dateTo]);

    const loadInitialData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await dispatch(fetchAIRequests({
                companyId: 'all',
                dateFrom: undefined,
                dateTo: undefined,
            }));

            if (fetchAIRequests.fulfilled.match(result)) {
                setRequests(result.payload as AICallMetrics[]);
            } else if (fetchAIRequests.rejected.match(result)) {
                dispatch(showError("error"))
            }
        } catch {
            dispatch(showError("error"))
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    useEffect(() => {
        setExpandedMessage(false);
        setExpandedResponse(false);
    }, [traceData]);

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;
        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleCompanyChange = (event) => {
        setCompany(event.target.value);
    };

    const handleRefreshClick = () => {
        setSelectedRow(null);
        fetchAIUsageData(company, dateFrom, dateTo);
    };

    const handleMonthFilterSubmit = (dates: { dateFrom: Date; dateTo: Date }) => {
        setDateFrom(dates.dateFrom);
        setDateTo(dates.dateTo);
    };

    const handleShowTrace = useCallback(async (traceId: string) => {
        setTraceLoading(true);
        try {
            const result = await dispatch(fetchAITrace(traceId));
            if (fetchAITrace.fulfilled.match(result)) {
                setTraceData(result.payload);
                const companyRow = requests.find(stat => stat.traceId === traceId);
                if (companyRow) {
                    setSelectedRow(companyRow);
                }
            } else {
                dispatch(showError("error"))
            }
        } catch (error) {
            dispatch(showError("error"))
        } finally {
            setTraceLoading(false);
        }
    }, [dispatch, requests]);

    const requestColumns: GridColDef<AICallMetrics>[] = [
        {
            field: 'companyName',
            headerName: l10n.getString("company", null, "Company"),
            flex: 1,
            valueGetter: (_, row) => {
                const company = companies.find(c => Number(c.id) === row.companyId);
                return company ? company.name : "";
            }
        },
        {
            field: 'inputTokens',
            headerName: "INPUT",
            width: 80
        },
        {
            field: 'outputTokens',
            headerName: "OUTPUT",
            width: 80
        },
        {
            field: 'totalTokens',
            headerName: "TOTAL",
            width: 80
        },
        {
            field: 'actions',
            type: 'actions',
            width: 200,
            renderCell: (params) => (
                <ButtonGroup>
                    <Button
                        color="primary"
                        onClick={() => {
                            setSelectedRow(params.row);
                            if (params.row.traceId) {
                                handleShowTrace(params.row.traceId);
                            }
                        }}
                    >
                        <Localized id="ai-usage-show-trace">
                            <span>_Pokaż szczegóły</span>
                        </Localized>
                    </Button>
                </ButtonGroup>
            )
        }
    ];

    const groupedData = useMemo(() => {
        if (!selectedRow) return [];
        const filtered = requests.filter(stat => stat.companyId === selectedRow.companyId);
        const grouped = filtered.reduce((acc, stat) => {
            const existing = acc.find(g => g.user === stat.userId);
            if (existing) {
                existing.inputTokens += stat.inputTokens;
                existing.outputTokens += stat.outputTokens;
                existing.totalTokens += stat.totalTokens;
            } else {
                acc.push({
                    user: stat.userId,
                    inputTokens: stat.inputTokens,
                    outputTokens: stat.outputTokens,
                    totalTokens: stat.totalTokens,
                });
            }
            return acc;
        }, [] as { user: string; inputTokens: number; outputTokens: number; totalTokens: number }[]);
        return grouped.sort((a, b) => b.totalTokens - a.totalTokens);
    }, [requests, selectedRow]);

    const summary = useMemo(() => {
        return groupedData.reduce((acc, g) => {
            acc.inputTokens += g.inputTokens;
            acc.outputTokens += g.outputTokens;
            acc.totalTokens += g.totalTokens;
            return acc;
        }, { inputTokens: 0, outputTokens: 0, totalTokens: 0 });
    }, [groupedData]);

    const userColumns: GridColDef<{ user: string; inputTokens: number; outputTokens: number; totalTokens: number }>[] = [
        {
            field: 'user',
            headerName: l10n.getString("user", null, "User"),
            flex: 1
        },
        {
            field: 'inputTokens',
            headerName: "INPUT",
            width: 80
        },
        {
            field: 'outputTokens',
            headerName: "OUTPUT",
            width: 80
        },
        {
            field: 'totalTokens',
            headerName: "TOTAL",
            width: 80
        }
    ];

    if (isLoading) {
        return <LoadingContainer />;
    }

    return (
        <>
            <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id="select-company-label">
                            <Localized id="company">Company</Localized>
                        </InputLabel>
                        <Select
                            value={company}
                            onChange={handleCompanyChange}
                            labelId="select-company-label"
                            label={<Localized id="company">Company</Localized>}
                        >
                            <MenuItem value={'all'}>
                                <Localized id="all">All</Localized>
                            </MenuItem>
                            {companies.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <MonthFilterMenu 
                        initialDateFrom={dateFrom} 
                        initialDateTo={dateTo} 
                        onSubmit={handleMonthFilterSubmit} 
                    />
                    <IconButton onClick={handleRefreshClick}>
                        <SyncIcon />
                    </IconButton>
            </Stack>

            <Grid container spacing={2} sx={{ height: 'calc(100vh - 280px)' }}>
                <Grid item xs={selectedRow ? 6 : 12} sx={{ height: '100%' }}>
                    <DataGrid
                        style={{ height: '100%' }}
                        localeText={getCustomLocaleText()}
                        rows={requests}
                        columns={requestColumns}
                        getRowId={(row) => row.id}
                        onRowClick={(params) => setSelectedRow(params.row)}
                        slots={{ toolbar: QuickSearchToolbar }}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'occurredAt', sort: 'desc' }],
                            },
                        }}
                    />
                </Grid>
                {selectedRow && (
                    <Grid item xs={6} sx={{ height: '100%', overflow: 'auto', padding: 2 }}>
                        <Paper sx={{ padding: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 2 }}>
                                <Typography variant="h6">{companies.find(f => Number(f.id) === selectedRow.companyId)?.name}</Typography>
                                <IconButton size="small" onClick={() => setSelectedRow(null)}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                            {traceLoading ? (
                                <LoadingContainer />
                            ) : traceData ? (
                                <Stack spacing={2}>
                                    <Card variant="outlined" sx={{ backgroundColor: '#f5f5f5' }}>
                                        <CardContent>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    <Localized id="ai-message">_Wiadomość:</Localized>
                                                </Typography>
                                                <IconButton size="small" onClick={() => setExpandedMessage(!expandedMessage)}>
                                                    {expandedMessage ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Stack>
                                            <Collapse in={expandedMessage} timeout={0}>
                                                <Typography variant="body2" sx={{ marginTop: 1, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                    {traceData.systemPrompt}
                                                </Typography>
                                                <Typography variant="body2" sx={{ marginTop: 1, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                    {traceData.userMessage}
                                                </Typography>
                                            </Collapse>
                                        </CardContent>
                                    </Card>
                                    <Card variant="outlined" sx={{ backgroundColor: '#f0f8f4' }}>
                                        <CardContent>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    <Localized id="ai-response">_Odpowiedź:</Localized>
                                                </Typography>
                                                <IconButton size="small" onClick={() => setExpandedResponse(!expandedResponse)}>
                                                    {expandedResponse ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Stack>
                                            <Collapse in={expandedResponse} timeout={0}>
                                                <Typography variant="body2" sx={{ marginTop: 1, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                    {traceData.responseText}
                                                </Typography>
                                            </Collapse>
                                        </CardContent>
                                    </Card>
                                    <Card sx={{ backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
                                        <CardContent>
                                            <Typography variant="body2" sx={{ marginBottom: 1, color: '#1565c0' }}>
                                                <Localized id="ai-summary">_Podsumowanie zużycia tokenów dla całej firmy (w wybranym okresie)</Localized>
                                            </Typography>
                                            <Stack spacing={1}>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2">
                                                        <Localized id="ai-settings-prompt-tokens">_Liczba tokenów zapytania:</Localized>
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{summary.inputTokens}</Typography>
                                                </Stack>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2">
                                                        <Localized id="ai-settings-completion-tokens">_Liczba tokenów odpowiedzi:</Localized>
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{summary.outputTokens}</Typography>
                                                </Stack>
                                                <Stack direction="row" justifyContent="space-between" sx={{ borderTop: '1px solid #90caf9', paddingTop: 1, marginTop: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        <Localized id="ai-settings-total-tokens">_Całkowita liczba tokenów:</Localized>
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1565c0' }}>{summary.totalTokens}</Typography>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                    <DataGrid
                                        rows={groupedData}
                                        columns={userColumns}
                                        getRowId={(row) => row.user}
                                        autoHeight
                                        localeText={getCustomLocaleText()}
                                    />
                                </Stack>
                            ) : (
                                <Typography>
                                    <Localized id="ai-select-trace">_Wybierz wiersz, aby wyświetlić szczegóły</Localized>
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </>
    );
};